import api from '@/lib/api'
import type { ColumnInfo } from '@/components/upload-wizard/types'

/**
 * Use case information from backend API
 */
export interface UseCaseInfo {
  name: string
  display_name: string
  category: string
  prediction_type: string
  description: string
  required_data_sources: string[]
  default_target_column: string
}

/**
 * Required field information for a use case
 */
export interface RequiredFieldInfo {
  name: string
  description: string
  required: boolean
  data_type: 'numeric' | 'categorical' | 'boolean' | 'datetime'
}

/**
 * Response from detect-use-case endpoint
 */
export interface DetectUseCaseResponse {
  detected_use_cases: string[]
  confidence_scores: Record<string, number>
  suggestions: string[]
}

/**
 * Response from use-case fields endpoint
 */
export interface UseCaseFieldsResponse {
  use_case: string
  display_name?: string
  required_fields: RequiredFieldInfo[]
  optional_fields: RequiredFieldInfo[]
}

// In-memory cache for use cases (avoid repeated API calls)
let useCaseCache: UseCaseInfo[] | null = null
let cacheTimestamp: number | null = null
const CACHE_DURATION_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Service for managing use cases
 */
export const useCasesService = {
  /**
   * Fetch all available use cases from the backend API
   * Results are cached for 5 minutes to avoid repeated API calls
   *
   * @param category - Optional category filter (e.g., 'ecommerce', 'parking')
   * @param forceRefresh - Force cache refresh
   * @returns Array of use case information
   */
  async listUseCases(category?: string, forceRefresh = false): Promise<UseCaseInfo[]> {
    const now = Date.now()
    const isCacheValid = useCaseCache !== null &&
                         cacheTimestamp !== null &&
                         (now - cacheTimestamp) < CACHE_DURATION_MS

    // Return cached data if valid and no category filter
    if (!forceRefresh && isCacheValid && !category) {
      return useCaseCache!
    }

    // Fetch from API
    const { data } = await api.get<UseCaseInfo[]>('/models/use-cases', {
      params: category ? { category } : undefined,
    })

    // Update cache only if fetching all use cases (no filter)
    if (!category) {
      useCaseCache = data
      cacheTimestamp = now
    }

    return data
  },

  /**
   * Get a single use case by name
   * Uses the cached list if available
   *
   * @param name - Use case name (e.g., 'ecommerce_churn')
   * @returns Use case information or undefined if not found
   */
  async getUseCase(name: string): Promise<UseCaseInfo | undefined> {
    const useCases = await this.listUseCases()
    return useCases.find(uc => uc.name === name)
  },

  /**
   * Get use cases by category
   *
   * @param category - Category name (e.g., 'ecommerce', 'parking')
   * @returns Array of use cases in the category
   */
  async getUseCasesByCategory(category: string): Promise<UseCaseInfo[]> {
    return this.listUseCases(category)
  },

  /**
   * Detect compatible use cases based on data source columns
   * Note: This endpoint may not exist in the backend yet
   *
   * @param dataSourceId - Data source ID
   * @param columns - Optional column information for more accurate detection
   * @returns Detected use cases with confidence scores
   */
  async detectCompatibleUseCases(
    dataSourceId: string,
    columns?: ColumnInfo[]
  ): Promise<DetectUseCaseResponse> {
    try {
      const { data } = await api.post<DetectUseCaseResponse>(
        '/data-sources/detect-use-case',
        {
          data_source_id: dataSourceId,
          columns: columns || undefined,
        }
      )
      return data
    } catch (error: any) {
      // Endpoint doesn't exist yet - return empty response
      console.warn('detect-use-case endpoint not available:', error.message)
      return {
        detected_use_cases: [],
        confidence_scores: {},
        suggestions: ['Unable to detect use cases automatically. Please select manually.'],
      }
    }
  },

  /**
   * Get required fields for a specific use case
   * Note: This endpoint may not exist in the backend yet
   *
   * @param useCase - Use case name
   * @returns Required and optional fields for the use case
   */
  async getUseCaseFields(useCase: string): Promise<UseCaseFieldsResponse> {
    try {
      const { data } = await api.get<UseCaseFieldsResponse>(
        `/models/use-cases/${useCase}/fields`
      )
      return data
    } catch (error: any) {
      // Endpoint doesn't exist yet - return basic fields from use case registry
      console.warn('use-case fields endpoint not available:', error.message)

      // Fallback: Use the use case info we have (use useCasesService to avoid `this` binding issues)
      const useCaseInfo = await useCasesService.getUseCase(useCase)
      if (!useCaseInfo) {
        throw new Error(`Use case not found: ${useCase}`)
      }

      // Return minimal field info based on default target column
      return {
        use_case: useCase,
        required_fields: [
          {
            name: useCaseInfo.default_target_column,
            description: `Target column for ${useCaseInfo.display_name}`,
            required: true,
            data_type: 'numeric',
          },
        ],
        optional_fields: [],
      }
    }
  },

  /**
   * Clear the use case cache
   * Useful after system updates or when fresh data is needed
   */
  clearCache(): void {
    useCaseCache = null
    cacheTimestamp = null
  },

  /**
   * Check if a use case exists
   *
   * @param name - Use case name
   * @returns True if use case exists
   */
  async exists(name: string): Promise<boolean> {
    const useCase = await this.getUseCase(name)
    return useCase !== undefined
  },

  /**
   * Get all unique categories from available use cases
   *
   * @returns Array of category names
   */
  async getCategories(): Promise<string[]> {
    const useCases = await this.listUseCases()
    const categories = new Set(useCases.map(uc => uc.category))
    return Array.from(categories).sort()
  },
}

// Export individual functions for convenience
export const {
  listUseCases,
  getUseCase,
  getUseCasesByCategory,
  detectCompatibleUseCases,
  getUseCaseFields,
  clearCache: clearUseCaseCache,
  exists: useCaseExists,
  getCategories: getUseCaseCategories,
} = useCasesService

// Default export
export default useCasesService
